import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    Box, Typography, Grid, CircularProgress, Card, CardContent, IconButton, InputBase,
    MenuItem, Select, Paper, Button
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';

const VoiceLibrary = () => {
    const [voices, setVoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [gender, setGender] = useState('');
    const [accent, setAccent] = useState('');
    const [provider, setProvider] = useState('11labs');

    const fetchVoices = async () => {
        try {
            const res = await axios.get('/api/vapi/voices');
            setVoices(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVoices();
    }, []);

    const filteredVoices = voices.filter(v =>
        v.name.toLowerCase().includes(search.toLowerCase()) &&
        (!gender || v.labels?.gender === gender) &&
        (!accent || v.labels?.accent === accent)
    );

    const featuredVoices = filteredVoices.slice(0, 4); // Sample featured voices

    return (
        <Box sx={{ bgcolor: '#0f0f0f', color: 'white', minHeight: '100vh', p: 4 }}>
            <Typography variant="h5" fontWeight={700} mb={2}>
                <VolumeUpIcon sx={{ mr: 1 }} />
                Voice Library
            </Typography>

            {/* Featured Voices */}
            <Typography variant="h6" fontWeight={600} mb={2}>Featured Voices</Typography>
            <Grid container spacing={2} sx={{ mb: 4 }}>
                {featuredVoices.map(voice => (
                    <Grid item xs={12} sm={6} md={3} key={voice.id}>
                        <Box
                            sx={{
                                position: 'relative',
                                bgcolor: '#1a1a1a',
                                borderRadius: 3,
                                overflow: 'hidden',
                                height: 200,
                                p: 2
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight={600}>
                                {voice.name}
                            </Typography>
                            <Typography variant="caption" sx={{ bgcolor: '#2c2c2c', px: 1, py: 0.5, borderRadius: 1 }}>
                                11LABS
                            </Typography>
                            <Typography variant="body2" mt={1}>
                                Best for: {voice.labels?.use_case || 'General'}
                            </Typography>
                            <IconButton
                                sx={{ position: 'absolute', bottom: 16, left: 16 }}
                                onClick={() => new Audio(voice.sample_url).play()}
                            >
                                <PlayArrowIcon sx={{ color: 'white' }} />
                            </IconButton>
                            <IconButton
                                sx={{ position: 'absolute', bottom: 16, right: 16 }}
                                onClick={() => navigator.clipboard.writeText(voice.id)}
                            >
                                <FileCopyIcon sx={{ color: 'white' }} />
                            </IconButton>
                        </Box>
                    </Grid>
                ))}
            </Grid>

            {/* Explore Section */}
            <Typography variant="h6" fontWeight={600} mb={2}>Explore all voices</Typography>

            {/* Filters */}
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={3}>
                <Paper component="form" sx={{ p: '2px 4px', display: 'flex', alignItems: 'center', width: 300, bgcolor: '#1e1e1e' }}>
                    <InputBase
                        sx={{ ml: 1, flex: 1, color: 'white' }}
                        placeholder="Search by keyword, id, use..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </Paper>
                <Select
                    value={provider}
                    onChange={e => setProvider(e.target.value)}
                    sx={{ minWidth: 120, bgcolor: '#1e1e1e', color: 'white' }}
                >
                    <MenuItem value="11labs">11labs</MenuItem>
                </Select>
                <Select
                    value={gender}
                    onChange={e => setGender(e.target.value)}
                    displayEmpty
                    sx={{ minWidth: 140, bgcolor: '#1e1e1e', color: 'white' }}
                >
                    <MenuItem value="">Select Gender</MenuItem>
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                </Select>
                <Select
                    value={accent}
                    onChange={e => setAccent(e.target.value)}
                    displayEmpty
                    sx={{ minWidth: 140, bgcolor: '#1e1e1e', color: 'white' }}
                >
                    <MenuItem value="">Select Accent</MenuItem>
                    <MenuItem value="american">American</MenuItem>
                    <MenuItem value="british">British</MenuItem>
                    <MenuItem value="indian">Indian</MenuItem>
                    <MenuItem value="australian">Australian</MenuItem>
                </Select>

                <Button variant="outlined" size="small" sx={{ color: 'white', borderColor: 'white' }}>+ Add</Button>
                <Button variant="outlined" size="small" sx={{ color: 'white', borderColor: 'white' }}>Clone</Button>
                <Button variant="outlined" size="small" sx={{ color: 'gray', borderColor: 'gray' }} disabled>Sync</Button>
            </Box>

            {/* Voice Grid */}
            {loading ? (
                <CircularProgress />
            ) : (
                <Grid container spacing={2}>
                    {filteredVoices.map(voice => (
                        <Grid item xs={12} sm={6} md={3} key={voice.id}>
                            <Card sx={{ bgcolor: '#1e1e1e', color: 'white' }}>
                                <CardContent>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        {voice.name}
                                    </Typography>
                                    <Typography variant="body2" color="gray">
                                        {voice.description}
                                    </Typography>
                                    <Typography variant="caption" display="block" mt={1}>
                                        $0.036/min ~400ms
                                    </Typography>
                                    <Box display="flex" justifyContent="space-between" mt={1}>
                                        <IconButton onClick={() => new Audio(voice.sample_url).play()}>
                                            <PlayArrowIcon sx={{ color: 'white' }} />
                                        </IconButton>
                                        <IconButton onClick={() => navigator.clipboard.writeText(voice.id)}>
                                            <FileCopyIcon sx={{ color: 'white' }} />
                                        </IconButton>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
};

export default VoiceLibrary;
